/**
 * Cita Events - Checkout Core Validation Module
 * ==============================================
 * WCAG 3.3.1/3.3.3 Compliant Form Validation
 *
 * This module provides unified form validation with:
 * - Live validation on blur/input
 * - ARIA-compliant error messaging
 * - Support for indexed fields (group/plus-one)
 * - Age verification with configurable min age
 *
 * @version 1.0.0
 * @module checkout-core/validation
 */

// Safe fallback: __ may not be available during module parse
const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };

/**
 * Validation rules for checkout forms.
 * Each rule specifies required status, validation pattern/logic, and messages.
 *
 * @constant {Object}
 */
export const validationRules = {
  email: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    messages: {
      required: __('Bitte gib deine E-Mail-Adresse ein.'),
      invalid: __('Bitte gib eine gültige E-Mail-Adresse ein.'),
      plusAlias: __('E-Mail-Adressen mit + sind nicht erlaubt.'),
    },
  },
  first_name: {
    required: true,
    minLength: 2,
    messages: {
      required: __('Bitte gib deinen Vornamen ein.'),
      minLength: __('Der Vorname muss mindestens 2 Zeichen haben.'),
    },
  },
  last_name: {
    required: true,
    minLength: 2,
    messages: {
      required: __('Bitte gib deinen Nachnamen ein.'),
      minLength: __('Der Nachname muss mindestens 2 Zeichen haben.'),
    },
  },
  birth_date: {
    required: true,
    messages: {
      required: __('Bitte gib dein Geburtsdatum ein.'),
      invalid: __('Bitte gib ein gültiges Geburtsdatum ein.'),
      underage: __('Du musst mindestens {minAge} Jahre alt sein.'),
    },
  },
  consent_dsgvo: {
    required: true,
    messages: {
      required: __('Bitte akzeptiere die Datenschutzerklärung.'),
    },
  },
  consent_agb: {
    required: true,
    messages: {
      required: __('Bitte akzeptiere die AGB.'),
    },
  },
  consent_organizer_terms: {
    required: false, // Conditional - only required if organizer has terms
    messages: {
      required: __('Bitte akzeptiere die Veranstalter-AGBs.'),
    },
  },
  membership_number: {
    required: false, // Dynamically determined from form data-membership-required
    messages: {
      required: __('Bitte gib deine Zusatzangabe an.'),
    },
  },
};

/**
 * Extract base field name from indexed field names.
 * E.g., "group_email_0" -> "email", "plus_one_birth_date_1" -> "birth_date"
 *
 * @param {string} name - The field name (possibly indexed)
 * @returns {string} The base field name
 */
export function getBaseFieldName(name) {
  if (!name) return '';

  // Handle group_* pattern (e.g., group_email_0, group_first_name_2)
  const groupMatch = name.match(/^group_(\w+)_\d+$/);
  if (groupMatch) {
    return groupMatch[1];
  }

  // Handle plus_one_* pattern (e.g., plus_one_email_0, plus_one_birth_date_1)
  const plusOneMatch = name.match(/^plus_one_(\w+)_\d+$/);
  if (plusOneMatch) {
    return plusOneMatch[1];
  }

  return name;
}

/**
 * Validate a single form field.
 *
 * @param {HTMLInputElement} input - The input element to validate
 * @param {Object} [options={}] - Validation options
 * @param {number} [options.minAge=0] - Minimum age for birth_date validation
 * @param {string} [options.ageVerificationMode='birth_date'] - Age verification mode
 * @returns {{valid: boolean, message?: string}} Validation result
 */
export function validateField(input, options = {}) {
  if (!input) return { valid: true };

  const { minAge = 0, ageVerificationMode = 'birth_date' } = options;
  const name = input.name || input.id || '';
  const baseName = getBaseFieldName(name);
  const rule = validationRules[baseName];

  if (!rule) return { valid: true };

  const value = input.type === 'checkbox' ? input.checked : (input.value || '').trim();

  // birth_date / age_confirmed are only required when age verification is active
  const isAgeField = baseName === 'birth_date' || baseName === 'age_confirmed';
  const ageVerificationActive = minAge > 0;

  // membership_number: required is dynamic based on form config
  let effectiveRequired;
  if (baseName === 'membership_number') {
    const form = input.closest('form');
    const membershipRequired = form?.dataset?.membershipRequired === '1' || form?.dataset?.membershipRequired === 'true';
    const hasMembership = form?.querySelector('input[name="has_membership_number"]')?.checked;
    effectiveRequired = membershipRequired || (hasMembership && rule.required);
  } else {
    effectiveRequired = isAgeField ? (rule.required && ageVerificationActive) : rule.required;
  }

  // Required check
  if (effectiveRequired) {
    if (input.type === 'checkbox' && !value) {
      return { valid: false, message: rule.messages.required };
    }
    if (input.type !== 'checkbox' && !value) {
      return { valid: false, message: rule.messages.required };
    }
  }

  // Skip further validation if empty and not required
  if (!value && !effectiveRequired) {
    return { valid: true };
  }

  // Pattern validation (email)
  if (rule.pattern && !rule.pattern.test(value)) {
    return { valid: false, message: rule.messages.invalid };
  }

  // +alias email rejection (business rule, bypassed by site_config allow_plus_alias_emails)
  if (baseName === 'email' && value && !window.citaBootData?.allowPlusAlias && value.split('@')[0].includes('+')) {
    return { valid: false, message: rule.messages.plusAlias || rule.messages.invalid };
  }

  // MinLength validation
  if (rule.minLength && value.length < rule.minLength) {
    return { valid: false, message: rule.messages.minLength };
  }

  // Birth date age validation
  if (baseName === 'birth_date' && ageVerificationMode === 'birth_date') {
    const eventTz = document.querySelector('[data-event-timezone]')?.dataset?.eventTimezone || null;
    const ageResult = validateBirthDate(value, minAge, eventTz);
    if (!ageResult.valid) {
      return ageResult;
    }
  }

  return { valid: true };
}

/**
 * Validate birth date against minimum age requirement.
 *
 * @param {string} value - Birth date string (YYYY-MM-DD)
 * @param {number} minAge - Minimum required age
 * @returns {{valid: boolean, message?: string}} Validation result
 */
export function validateBirthDate(value, minAge, timezone) {
  if (!value) {
    return {
      valid: false,
      message: validationRules.birth_date.messages.required,
    };
  }

  const birth = new Date(value);
  if (Number.isNaN(birth.getTime())) {
    return {
      valid: false,
      message: validationRules.birth_date.messages.invalid,
    };
  }

  // W-06 FIX: Use event timezone if available, fall back to browser timezone.
  // This is informational only — the authoritative age check happens server-side
  // in guest_registration_api.py using _get_now_in_event_timezone().
  let todayStr;
  if (timezone) {
    try {
      todayStr = new Intl.DateTimeFormat('sv-SE', { timeZone: timezone }).format(new Date());
    } catch {
      todayStr = new Date().toISOString().slice(0, 10);
    }
  } else {
    todayStr = new Date().toISOString().slice(0, 10);
  }
  const todayParts = todayStr.split('-').map(Number);
  const birthParts = [birth.getFullYear(), birth.getMonth() + 1, birth.getDate()];

  let age = todayParts[0] - birthParts[0];
  if (todayParts[1] < birthParts[1] ||
      (todayParts[1] === birthParts[1] && todayParts[2] < birthParts[2])) {
    age -= 1;
  }

  if (age < minAge) {
    return {
      valid: false,
      message: validationRules.birth_date.messages.underage.replace('{minAge}', minAge),
    };
  }

  return { valid: true };
}

/**
 * P1-5 FIX: Validate group booking quantity against ticket min/max constraints.
 * Plus-one guests do NOT count towards group size.
 *
 * @param {Object} ticketData - Ticket configuration object
 * @param {number} quantity - Selected quantity (excluding plus-ones)
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateGroupSize(ticketData, quantity) {
  if (!ticketData || !ticketData.allow_group_booking) {
    return { valid: true };
  }
  const min = parseInt(ticketData.group_min_size, 10) || 1;
  const max = parseInt(ticketData.group_max_size, 10) || 99;

  if (quantity < min) {
    return {
      valid: false,
      message: __('Dieses Ticket kann nur für mindestens {0} Personen gebucht werden.', [min]),
    };
  }
  if (quantity > max) {
    return {
      valid: false,
      message: __('Maximal {0} Personen pro Buchung möglich.', [max]),
    };
  }
  return { valid: true };
}

/**
 * Show validation error on a form field with ARIA compliance.
 *
 * @param {HTMLInputElement} input - The input element
 * @param {string} message - Error message to display
 */
export function showFieldError(input, message) {
  if (!input) return;

  // Template-aware error styling via CSS [aria-invalid="true"] selector
  // No Tailwind border classes - event-checkout.css handles styling via [aria-invalid]
  input.setAttribute('aria-invalid', 'true');

  // Find or create error container
  const wrapper = input.closest('.form-group')
    || (input.type === 'checkbox' ? input.closest('label') : null)
    || input.parentElement;
  if (!wrapper) return;

  let errorEl = wrapper.querySelector('.checkout-field-error');
  if (!errorEl) {
    errorEl = document.createElement('div');
    errorEl.className = 'checkout-field-error';
    errorEl.setAttribute('role', 'status');
    errorEl.setAttribute('aria-live', 'polite');

    // Generate unique ID for ARIA association
    const errorId = `error-${input.name || input.id || Math.random().toString(36).slice(2)}`;
    errorEl.id = errorId;
    input.setAttribute('aria-describedby', errorId);

    // Static warning icon (no user content — safe as innerHTML)
    errorEl.innerHTML = '<svg aria-hidden="true" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" viewBox="0 0 24 24" style="flex-shrink:0">'
      + '<circle cx="12" cy="12" r="10"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>';
    const msgSpan = document.createElement('span');
    errorEl.appendChild(msgSpan);
    wrapper.appendChild(errorEl);
  }

  // Update message text safely (no innerHTML for user content)
  const msgSpan = errorEl.querySelector('span') || errorEl;
  msgSpan.textContent = message;
  errorEl.classList.remove('hidden');
}

/**
 * Clear validation error from a form field.
 *
 * @param {HTMLInputElement} input - The input element
 */
export function clearFieldError(input) {
  if (!input) return;

  // Explicitly mark as valid (signals correction to screen readers)
  input.setAttribute('aria-invalid', 'false');

  // Hide error message
  const wrapper = input.closest('.form-group')
    || (input.type === 'checkbox' ? input.closest('label') : null)
    || input.parentElement;
  if (!wrapper) return;

  const errorEl = wrapper.querySelector('.checkout-field-error');
  if (errorEl) {
    const msgSpan = errorEl.querySelector('span');
    if (msgSpan) {
      msgSpan.textContent = '';
    }
    errorEl.classList.add('hidden');
  }
}

/**
 * Initialize live validation on a form.
 * Adds blur and input event listeners for real-time validation feedback.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} [options={}] - Validation options
 * @param {number} [options.minAge=0] - Minimum age for birth_date validation
 * @param {string} [options.ageVerificationMode='birth_date'] - Age verification mode
 */
export function initLiveValidation(form, options = {}) {
  if (!form) return;

  const validatableInputs = form.querySelectorAll('input, select, textarea');

  validatableInputs.forEach((input) => {
    const baseName = getBaseFieldName(input.name || input.id);
    if (!validationRules[baseName]) return;

    // Validate on blur
    input.addEventListener('blur', () => {
      const result = validateField(input, options);
      if (!result.valid) {
        showFieldError(input, result.message);
      } else {
        clearFieldError(input);
      }
    });

    // Clear error on input (while typing)
    input.addEventListener('input', () => {
      // Only clear if currently showing error
      if (input.getAttribute('aria-invalid') === 'true') {
        const result = validateField(input, options);
        if (result.valid) {
          clearFieldError(input);
        }
      }
    });

    // For checkboxes, validate on change
    if (input.type === 'checkbox') {
      input.addEventListener('change', () => {
        const result = validateField(input, options);
        if (!result.valid) {
          showFieldError(input, result.message);
        } else {
          clearFieldError(input);
        }
      });
    }
  });
}

/**
 * Validate entire form before submission.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} [options={}] - Validation options
 * @param {number} [options.minAge=0] - Minimum age for birth_date validation
 * @param {string} [options.ageVerificationMode='birth_date'] - Age verification mode
 * @param {boolean} [options.requireOrganizerTerms=false] - Whether organizer terms are required
 * @returns {boolean} True if form is valid
 */
export function validateForm(form, options = {}) {
  if (!form) return false;

  const { requireOrganizerTerms = false } = options;
  let isValid = true;
  let firstInvalidInput = null;

  const validatableInputs = form.querySelectorAll('input, select, textarea');

  validatableInputs.forEach((input) => {
    const baseName = getBaseFieldName(input.name || input.id);
    if (!validationRules[baseName]) return;

    // Special handling for organizer terms
    if (baseName === 'consent_organizer_terms' && !requireOrganizerTerms) {
      return;
    }

    const result = validateField(input, options);
    if (!result.valid) {
      showFieldError(input, result.message);
      isValid = false;
      if (!firstInvalidInput) {
        firstInvalidInput = input;
      }
    } else {
      clearFieldError(input);
    }
  });

  // Focus first invalid field for accessibility
  if (firstInvalidInput) {
    try {
      // Auto-expand collapsed <details> so the invalid field becomes visible
      const closedDetails = firstInvalidInput.closest('details:not([open])');
      if (closedDetails) closedDetails.open = true;

      firstInvalidInput.focus();
      firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      // Ignore scroll errors
    }
  }

  // Announce error count to screen readers (WCAG 3.3.1)
  announceValidationResult(form, isValid);

  return isValid;
}

/**
 * Announce validation result via an aria-live region.
 * Creates the region lazily on first use.
 */
function announceValidationResult(form, isValid) {
  let liveRegion = form.querySelector('[data-validation-announce]');
  if (!liveRegion) {
    liveRegion = document.createElement('div');
    liveRegion.setAttribute('data-validation-announce', '');
    liveRegion.setAttribute('aria-live', 'assertive');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.setAttribute('role', 'status');
    liveRegion.className = 'checkout-sr-only';
    form.prepend(liveRegion);
  }

  if (isValid) {
    liveRegion.textContent = '';
  } else {
    const errorCount = form.querySelectorAll('[aria-invalid="true"]').length;
    liveRegion.textContent = errorCount === 1
      ? __('1 Fehler gefunden. Bitte korrigiere das markierte Feld.')
      : __( '{0} Fehler gefunden. Bitte korrigiere die markierten Felder.', [errorCount]);
  }
}

/**
 * Check if email format is valid.
 *
 * @param {string} email - Email to validate
 * @returns {boolean} True if valid email format
 */
export function isValidEmail(email) {
  if (!email) return false;
  const trimmed = email.trim();
  return validationRules.email.pattern.test(trimmed) && (window.citaBootData?.allowPlusAlias || !trimmed.split('@')[0].includes('+'));
}

/**
 * Canonicalize email for duplicate comparison.
 * JS port of Python canonicalize_email() — strips +tags, lowercases, trims.
 * GC-21 FIX: Must match backend normalization to prevent validation drift.
 *
 * @param {string} email - Email address
 * @returns {string} Canonical email (empty string for invalid input)
 */
export function canonicalizeEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) {
    return String(email || '').trim().toLowerCase();
  }
  const cleaned = email.trim().toLowerCase();
  const atIdx = cleaned.indexOf('@');
  let local = cleaned.substring(0, atIdx);
  const domain = cleaned.substring(atIdx + 1);
  if (!window.citaBootData?.allowPlusAlias) {
    local = local.replace(/\+.*$/, '');
  }
  if (!local) {
    return cleaned;
  }
  return `${local}@${domain}`;
}

/**
 * Field length limits matching backend CUSTOM_FIELD_TEXT_MAX.
 * @constant {Object}
 */
const CUSTOM_FIELD_MAX_LENGTH = {
  text: 255,
  textarea: 2000,
  number: 20,
  date: 10,
  select: 255,
  checkbox: 1,
};

/**
 * Valid custom field types matching backend CUSTOM_FIELD_TYPE_MAP.
 * @constant {Set}
 */
const VALID_CUSTOM_FIELD_TYPES = new Set([
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'checkbox',
]);

/**
 * Generate dynamic validation rules for organizer-defined custom fields.
 * Creates rules based on field definitions from the ticket configuration.
 *
 * @param {Array} fieldDefinitions - Custom field definitions from ticket config
 * @returns {Object} Validation rules keyed by input name (custom_{fieldKey})
 *
 * @example
 * const fields = [
 *   { name: 'company', field_label: 'Firma', type: 'text', is_required: true },
 *   { name: 'diet', field_label: 'Ernährung', type: 'select', is_required: false }
 * ];
 * const rules = generateCustomFieldRules(fields);
 * // Returns: { custom_company: { required: true, maxLength: 255, ... }, ... }
 */
export function generateCustomFieldRules(fieldDefinitions) {
  const rules = {};

  if (!Array.isArray(fieldDefinitions) || fieldDefinitions.length === 0) {
    return rules;
  }

  fieldDefinitions.forEach((field) => {
    // Extract field key (prefer name, fallback to field_label)
    const fieldKey = (field.name || field.field_label || field.label || '').trim();
    if (!fieldKey) return;

    // Sanitize field key for input name
    const sanitizedKey = fieldKey.replace(/[^a-zA-Z0-9_-]/g, '_');
    const inputName = `custom_${sanitizedKey}`;

    // Normalize field type
    const fieldType = String(field.type || field.field_type || 'text').toLowerCase();

    // Skip unknown field types (security: whitelist approach)
    if (!VALID_CUSTOM_FIELD_TYPES.has(fieldType)) {
      return;
    }

    // Determine if required
    const isRequired = Boolean(field.required || field.is_required);

    // Get display label for error messages
    const displayLabel = field.label || field.field_label || __('Feld');

    // Build rule based on field type
    const rule = {
      required: isRequired,
      messages: {
        required: __('Bitte {0} ausfüllen.').replace('{0}', displayLabel),
      },
    };

    // Type-specific validation
    switch (fieldType) {
      case 'text':
        rule.maxLength = CUSTOM_FIELD_MAX_LENGTH.text;
        rule.messages.maxLength = __('{0} darf maximal {1} Zeichen haben.').replace('{0}', displayLabel).replace('{1}', CUSTOM_FIELD_MAX_LENGTH.text);
        break;

      case 'textarea':
        rule.maxLength = CUSTOM_FIELD_MAX_LENGTH.textarea;
        rule.messages.maxLength = __('{0} darf maximal {1} Zeichen haben.').replace('{0}', displayLabel).replace('{1}', CUSTOM_FIELD_MAX_LENGTH.textarea);
        break;

      case 'number':
        rule.pattern = /^-?\d+([.,]\d+)?$/;
        rule.messages.invalid = __('{0} muss eine gültige Zahl sein.').replace('{0}', displayLabel);
        break;

      case 'date':
        rule.pattern = /^\d{4}-\d{2}-\d{2}$/;
        rule.messages.invalid = __('{0} muss ein gültiges Datum sein (JJJJ-MM-TT).').replace('{0}', displayLabel);
        break;

      case 'select':
        // Select validation: value must not be empty placeholder
        rule.messages.required = __('Bitte wähle eine Option für {0}.').replace('{0}', displayLabel);
        break;

      case 'checkbox':
        // Checkbox: if required, must be checked
        rule.messages.required = __('Bitte bestätige {0}.').replace('{0}', displayLabel);
        break;
    }

    rules[inputName] = rule;
  });

  return rules;
}

/**
 * Validate a single custom field value against its rule.
 *
 * @param {HTMLElement} input - The input element
 * @param {Object} rule - The validation rule from generateCustomFieldRules
 * @returns {Object} Validation result { valid: boolean, message?: string }
 */
export function validateCustomField(input, rule) {
  if (!input || !rule) {
    return { valid: true };
  }

  const isCheckbox = input.type === 'checkbox';
  const value = isCheckbox ? input.checked : (input.value || '').trim();

  // Required check
  if (rule.required) {
    if (isCheckbox && !value) {
      return { valid: false, message: rule.messages?.required || __('Dieses Feld ist erforderlich.') };
    }
    if (!isCheckbox && value === '') {
      return { valid: false, message: rule.messages?.required || __('Dieses Feld ist erforderlich.') };
    }
  }

  // Skip further validation if empty and not required
  if (!isCheckbox && value === '') {
    return { valid: true };
  }

  // Max length check (for text fields)
  if (rule.maxLength && typeof value === 'string' && value.length > rule.maxLength) {
    return { valid: false, message: rule.messages?.maxLength || __('Maximal {0} Zeichen erlaubt.').replace('{0}', rule.maxLength) };
  }

  // Pattern check (for number, date, etc.)
  if (rule.pattern && !rule.pattern.test(value)) {
    return { valid: false, message: rule.messages?.invalid || __('Ungültiger Wert.') };
  }

  return { valid: true };
}

/**
 * Initialize live validation for custom fields on a form.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} customRules - Rules from generateCustomFieldRules
 */
export function initCustomFieldValidation(form, customRules) {
  if (!form || !customRules || Object.keys(customRules).length === 0) {
    return;
  }

  Object.keys(customRules).forEach((inputName) => {
    const input = form.querySelector(`[name="${inputName}"]`);
    if (!input) return;

    const rule = customRules[inputName];

    // Blur validation
    input.addEventListener('blur', () => {
      const result = validateCustomField(input, rule);
      if (!result.valid) {
        showFieldError(input, result.message);
      } else {
        clearFieldError(input);
      }
    });

    // Clear error on input (immediate feedback)
    input.addEventListener('input', () => {
      if (input.getAttribute('aria-invalid') === 'true') {
        const result = validateCustomField(input, rule);
        if (result.valid) {
          clearFieldError(input);
        }
      }
    });

    // For checkboxes, also listen to change
    if (input.type === 'checkbox') {
      input.addEventListener('change', () => {
        const result = validateCustomField(input, rule);
        if (!result.valid) {
          showFieldError(input, result.message);
        } else {
          clearFieldError(input);
        }
      });
    }
  });
}

/**
 * Validate all custom fields on a form.
 *
 * @param {HTMLFormElement} form - The form element
 * @param {Object} customRules - Rules from generateCustomFieldRules
 * @returns {boolean} True if all custom fields are valid
 */
export function validateCustomFields(form, customRules) {
  if (!form || !customRules || Object.keys(customRules).length === 0) {
    return true;
  }

  let isValid = true;
  let firstInvalidInput = null;

  Object.keys(customRules).forEach((inputName) => {
    const input = form.querySelector(`[name="${inputName}"]`);
    if (!input) return;

    const rule = customRules[inputName];
    const result = validateCustomField(input, rule);

    if (!result.valid) {
      showFieldError(input, result.message);
      isValid = false;
      if (!firstInvalidInput) {
        firstInvalidInput = input;
      }
    } else {
      clearFieldError(input);
    }
  });

  // Focus first invalid field for accessibility
  if (firstInvalidInput) {
    try {
      // Auto-expand collapsed <details> so the invalid field becomes visible
      const closedDetails = firstInvalidInput.closest('details:not([open])');
      if (closedDetails) closedDetails.open = true;

      firstInvalidInput.focus();
      firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      // Ignore scroll errors
    }
  }

  return isValid;
}

export default {
  validationRules,
  getBaseFieldName,
  validateField,
  validateBirthDate,
  showFieldError,
  clearFieldError,
  initLiveValidation,
  validateForm,
  isValidEmail,
  canonicalizeEmail,
  generateCustomFieldRules,
  validateCustomField,
  initCustomFieldValidation,
  validateCustomFields,
};
